/**
 * The checkout stepper.
 *
 * =====================================================================
 * HOW PROGRESS SURVIVES A REFRESH  (read this before touching the flow)
 * =====================================================================
 *
 * Requirement: refresh must never lose progress. The naive implementation keeps
 * `const [step, setStep] = useState(0)` in a wizard component, and it fails the
 * requirement three ways at once — F5 resets to step 1, the back button leaves the
 * flow entirely, and a shared/reopened link is meaningless.
 *
 * So step state is NEVER held in React state. It is derived, every render, from
 * two sources that both survive a reload:
 *
 *   1. THE URL. The pathname names the step (/order/options), and search params
 *      carry the ids of the server-side objects created so far:
 *        ?u=<uploadId>  after the file is uploaded and validated
 *        ?q=<quoteId>   after a price is quoted
 *        ?o=<orderId>   after checkout creates the order
 *      `useSearchParams` reads them; navigation preserves them (see
 *      `stepUrl` below). A refresh re-parses the same URL and lands on the same
 *      step with the same ids.
 *
 *   2. SERVER STATE, fetched by TanStack Query keyed on those ids. Whether a step
 *      is *complete* is a fact about the server's data, not about what the user
 *      clicked: "upload is done" means GET /uploads/{u} says status=validated;
 *      "options are chosen" means the quote exists and its line items match. The
 *      client re-derives that on every load, so a stale or hand-edited URL cannot
 *      fake progress.
 *
 * What is deliberately NOT used:
 *   - localStorage / sessionStorage. An order half-persisted in web storage
 *     desynchronises from the server the moment the quote expires or the file is
 *     purged, and it leaks a customer's order to the next person on a shared
 *     device. (An eslint rule blocks both APIs.)
 *   - A React context holding the whole draft order. Fine as a render-time cache;
 *     useless as the source of truth, because it dies on reload.
 *
 * The consequences to preserve as steps get implemented:
 *   - Every "Continue" is a `navigate(stepUrl(next, params))`, never a setState.
 *   - Deep-linking to a step whose prerequisites are unmet must REDIRECT to the
 *     furthest step that is satisfied (see `firstIncompleteStep`), not render a
 *     broken page.
 *   - No step may hold data the server does not have. If a field must survive
 *     refresh, it belongs in a PATCH to the server before "Continue" resolves.
 */
import { paths, searchParamKeys } from '../paths';

export type OrderStepId = 'upload' | 'review' | 'options' | 'details' | 'address' | 'pay';

export interface OrderStepDefinition {
  id: OrderStepId;
  path: string;
  /** Stepper label. Short: it renders at 375px. */
  label: string;
  /** Page <h1>. */
  title: string;
  /**
   * Which URL ids must be present for this step to be reachable. Server state
   * decides whether those ids are actually *valid*; this is the cheap first gate.
   */
  requires: (keyof typeof searchParamKeys)[];
}

export const ORDER_STEPS: readonly OrderStepDefinition[] = [
  {
    id: 'upload',
    path: paths.order.upload,
    label: 'Upload',
    title: 'Upload your file',
    requires: [],
  },
  {
    id: 'review',
    path: paths.order.review,
    label: 'Review',
    title: 'Review your file',
    requires: ['upload'],
  },
  {
    id: 'options',
    path: paths.order.options,
    label: 'Options',
    title: 'Print options',
    requires: ['upload'],
  },
  {
    id: 'details',
    path: paths.order.details,
    label: 'Details',
    title: 'Your details',
    requires: ['upload', 'quote'],
  },
  {
    id: 'address',
    path: paths.order.address,
    label: 'Address',
    title: 'Delivery address',
    requires: ['upload', 'quote'],
  },
  {
    id: 'pay',
    path: paths.order.pay,
    label: 'Pay',
    title: 'Payment',
    requires: ['upload', 'quote'],
  },
] as const;

export function stepIndexById(id: OrderStepId): number {
  return ORDER_STEPS.findIndex((step) => step.id === id);
}

export function stepByPath(pathname: string): OrderStepDefinition | undefined {
  return ORDER_STEPS.find((step) => pathname === step.path || pathname.startsWith(`${step.path}/`));
}

/** Ids currently present in the URL. The only progress store there is. */
export type OrderUrlState = Partial<Record<keyof typeof searchParamKeys, string>>;

export function readOrderUrlState(params: URLSearchParams): OrderUrlState {
  const state: OrderUrlState = {};
  for (const [name, key] of Object.entries(searchParamKeys) as [
    keyof typeof searchParamKeys,
    string,
  ][]) {
    const value = params.get(key);
    if (value) state[name] = value;
  }
  return state;
}

/**
 * Builds the URL for a step, carrying the progress ids forward.
 *
 * Every in-flow navigation must go through this. A plain `navigate('/order/pay')`
 * drops the ids and silently resets the customer to the beginning.
 */
export function stepUrl(step: OrderStepId, state: OrderUrlState): string {
  const definition = ORDER_STEPS[stepIndexById(step)];
  const params = new URLSearchParams();
  for (const [name, key] of Object.entries(searchParamKeys) as [
    keyof typeof searchParamKeys,
    string,
  ][]) {
    const value = state[name];
    if (value) params.set(key, value);
  }
  const query = params.toString();
  const path = definition?.path ?? paths.order.upload;
  return query ? `${path}?${query}` : path;
}

/** True when the URL carries everything this step needs to render at all. */
export function isStepReachable(step: OrderStepDefinition, state: OrderUrlState): boolean {
  return step.requires.every((requirement) => Boolean(state[requirement]));
}

/**
 * The furthest step the current URL state can legitimately reach — where a
 * deep-link with missing prerequisites gets redirected to.
 */
export function firstIncompleteStep(state: OrderUrlState): OrderStepDefinition {
  const firstUnreachable = ORDER_STEPS.findIndex((step) => !isStepReachable(step, state));
  const fallbackIndex = firstUnreachable === -1 ? ORDER_STEPS.length - 1 : firstUnreachable - 1;
  return ORDER_STEPS[Math.max(0, fallbackIndex)] ?? ORDER_STEPS[0]!;
}
